import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  GalleryInvitationDto,
  GalleryMemberAccess,
  GalleryMemberDto,
  GalleryTenantAccessLevel,
  MemberDto,
} from '@picflow/types';
import { Observable, catchError, delay, map, of, tap } from 'rxjs';
import { galleryMembersMaxAge } from './consts';
import { AppConfigService } from './providers/config.service';
import { GalleryPasswordService } from './services/gallery-password.service';
import { GetAllService } from './services/get-all.service';
import { ObservableDataLoaderService } from './services/observable-data-loader.service';
import { DisplayUserEmailDto } from './services/visitor.service';

@Injectable({
  providedIn: 'root',
})
export class GalleryMemberService {
  private http = inject(HttpClient);
  private config = inject(AppConfigService);
  private loader = inject(ObservableDataLoaderService);
  private galleryPasswordService = inject(GalleryPasswordService);
  private getAllService = inject(GetAllService);

  public getGalleryMembers(galleryId: string): Observable<GalleryMemberDto[]> {
    return this.loader
      .get(
        'gallery_members_' + galleryId,
        () =>
          this.getAllService.getAll<GalleryMemberDto>(
            `${
              this.config.apiEndpoint
            }/gallery_members?gallery=${galleryId}&expand[]=data.user&ignore_401_error=true&ignore_403_error=true${this.getPassword(
              galleryId
            )}`
          ),
        galleryMembersMaxAge
      )
      .pipe(
        catchError(err => {
          if (!(err instanceof HttpErrorResponse)) throw err;
          if (err.status !== 401 && err.status !== 403) throw err;
          return of([]);
        })
      );
  }

  public getAllGalleryMembers(unique: boolean): Observable<GalleryMemberDto[]> {
    return this.loader
      .get(
        'gallery_members',
        () =>
          this.getAllService.getAll<GalleryMemberDto>(
            `${this.config.apiEndpoint}/gallery_members?expand[]=data.user&ignore_401_error=true`
          ),
        galleryMembersMaxAge
      )
      .pipe(
        catchError(err => {
          if (!(err instanceof HttpErrorResponse)) throw err;
          if (err.status !== 401 && err.status !== 403) throw err;
          return of([]);
        }),
        map(members => (unique ? this.makeMembersUnique(members) : members))
      );
  }

  public getPendingGalleryMembers(galleryId: string): Observable<GalleryInvitationDto[]> {
    return this.loader
      .get(
        `gallery_invitations_${galleryId}`,
        () =>
          this.getAllService.getAll<GalleryInvitationDto>(
            `${this.config.apiEndpoint}/gallery_invitations?expand[]=data.user&ignore_401_error=true&gallery=${galleryId}`
          ),
        galleryMembersMaxAge
      )
      .pipe(
        catchError(err => {
          if (!(err instanceof HttpErrorResponse)) throw err;
          if (err.status !== 401 && err.status !== 403) throw err;
          return of([]);
        })
      );
  }

  public getAllPendingGalleryMembers(): Observable<GalleryInvitationDto[]> {
    return this.loader
      .get(
        'gallery_invitations',
        () =>
          this.getAllService.getAll<GalleryInvitationDto>(
            `${this.config.apiEndpoint}/gallery_invitations?expand[]=data.user&ignore_401_error=true`
          ),
        galleryMembersMaxAge
      )
      .pipe(
        catchError(err => {
          if (!(err instanceof HttpErrorResponse)) throw err;
          if (err.status !== 401 && err.status !== 403) throw err;
          return of([]);
        })
      );
  }

  public getGalleryMembersForUser(userId: string): Observable<GalleryMemberDto[]> {
    return this.getAllGalleryMembers(false).pipe(map(members => members.filter(m => m.user['id'] === userId)));
  }

  public inviteMember(
    email: string,
    galleryId: string,
    accessLevel: GalleryTenantAccessLevel,
    notify: boolean,
    message: string
  ) {
    const params = {
      email,
      gallery: galleryId,
      access: accessLevel,
      message: message || undefined,
      notify: notify,
    };
    return this.http.post(`${this.config.apiEndpoint}/gallery_invitations`, params).pipe(
      delay(500), // giving the backend time to auto accept the invite
      tap(() => {
        this.loader.forceDataRefreshFuzzy('gallery_members');
        this.loader.forceDataRefreshFuzzy('gallery_invitations');
      })
    );
  }

  public updateMember(memberId: string, accessLevel: GalleryMemberAccess) {
    const params = {
      access: accessLevel,
    };
    return this.http.patch(`${this.config.apiEndpoint}/gallery_members/${memberId}`, params).pipe(
      tap(() => {
        this.loader.updateFuzzy<GalleryMemberDto[]>('gallery_members', members => {
          const member = members.find(m => m.id === memberId);
          if (!member) return members;
          member.access = accessLevel;
          return members;
        });
      })
    );
  }

  public updateInvite(inviteId: string, accessLevel: GalleryMemberAccess) {
    const params = {
      access: accessLevel,
    };
    return this.http.patch(`${this.config.apiEndpoint}/gallery_invitations/${inviteId}`, params).pipe(
      map(resp => {
        this.loader.updateFuzzy<GalleryInvitationDto[]>('gallery_invitations', invites => {
          const invite = invites.find(i => i.id === inviteId);
          if (!invite) return invites;
          invite.access = accessLevel;
          return invites;
        });
        return resp;
      })
    );
  }

  public removeMember(memberId: string) {
    return this.http.delete(`${this.config.apiEndpoint}/gallery_members/${memberId}`).pipe(
      tap(() => {
        this.loader.updateFuzzy<MemberDto[]>('gallery_members', members =>
          (members || []).filter(m => m.id !== memberId)
        );
      })
    );
  }

  public removeInvite(inviteId: string) {
    return this.http.delete(`${this.config.apiEndpoint}/gallery_invitations/${inviteId}`).pipe(
      tap(() => {
        this.loader.updateFuzzy<GalleryInvitationDto[]>('gallery_invitations', invites =>
          (invites || []).filter(m => m.id !== inviteId)
        );
      })
    );
  }

  private getPassword(galleryId: string) {
    const password = this.galleryPasswordService.getPassword(galleryId);
    if (!password) return '';
    return '&password=' + encodeURIComponent(password);
  }

  makeMembersUnique(members: GalleryMemberDto[]) {
    const uniqueMembers: GalleryMemberDto[] = [];
    members.forEach(member => {
      const user = member.user as DisplayUserEmailDto;
      if (!uniqueMembers.find(m => m.user['id'] === user.id)) {
        uniqueMembers.push(member);
      }
    });
    return uniqueMembers;
  }

  makeInvitesUnique(invites: GalleryInvitationDto[]) {
    const uniqueInvites: GalleryInvitationDto[] = [];
    invites.forEach(invite => {
      if (!uniqueInvites.find(m => m.email === invite.email)) {
        uniqueInvites.push(invite);
      }
    });
    return uniqueInvites;
  }
}
